import Icon from '../../Utilites/Icon';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Listen3Play - Komponenta pro otázku typu "listen3"
 * Props:
 *  - question: {
 *      audios: [audio1, audio2, audio3],
 *      options: [[img1, img2, img3], ...],
 *      correctIndex,
 *      correctWords,
 *      userAnswer,
 *      status
 *    }
 *  - handleAnswer: (index) => void
 *  - userAnswer: číslo nebo null
 *  - disabled: bool
 */
const Listen3Play = ({ question, handleAnswer, userAnswer, disabled }) => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState([false, false, false]);
  // Ref to track all currently playing Audio objects
  const audioRefs = React.useRef([]);

  // Helper to stop all currently playing Audio objects
  const stopAllAudios = () => {
    audioRefs.current.forEach(audio => {
      if (audio && typeof audio.pause === 'function') {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    audioRefs.current = [];
  };

  // Přehrání zvuku
  const playAudio = (audioUrl, idx) => {
    if (!audioUrl) return;
    stopAllAudios();
    const audio = new window.Audio(audioUrl);
    audioRefs.current = [audio];
    setPlaying(p => p.map((val, i) => i === idx ? true : val));
    audio.play();
    audio.onended = () => setPlaying(p => p.map((val, i) => i === idx ? false : val));
  };

  // Přehrání všech zvuků po sobě
  const playAllAudios = async () => {
    stopAllAudios();
    audioRefs.current = [];
    for (let i = 0; i < question.audios.length; i++) {
      const audioUrl = question.audios[i];
      if (!audioUrl) continue;
      setPlaying(p => p.map((val, idx) => idx === i ? true : val));
      await new Promise((resolve) => {
        const audio = new window.Audio(audioUrl);
        audioRefs.current.push(audio);
        audio.play();
        audio.onended = () => {
          setPlaying(p => p.map((val, idx) => idx === i ? false : val));
          resolve();
        };
      });
    }
  };

  // Automatické přehrání všech zvuků při změně otázky, ale ne při prvním mountu
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (question && question.audios && question.audios.length > 0) {
      playAllAudios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id]);

  // Wrapper for answer selection: stop all sounds before answering
  const handleAnswerWithStop = (optIdx) => {
    stopAllAudios();
    handleAnswer(optIdx);
  };

  return (
    <div className="listen3-play">
        <div className="flex gap-4 my-6 justify-center"> 
            <h2 className="question">{t('quiz-listen3-question')}</h2>
        </div>  

      <div className="flex justify-center mb-6">
        <button
          className={`btn-primary`}
          onClick={playAllAudios}
          disabled={disabled}
          aria-label={t('play-sound')}
        >
          <Icon name="volume_up" /> {t('play-sound-again')}
        </button>
      </div>  
      <div className="flex gap-1 mb-4 justify-center transform scale-[0.7] hidden">      
        {question.audios.map((audioUrl, idx) => (
          <button
            key={idx}
            className={`${playing[idx] ? 'btn-green' : 'btn-primary'}`}
            onClick={() => playAudio(audioUrl, idx)}
            disabled={disabled}
            aria-label={`${t('play-sound')} ${idx + 1}`}
          >
            <Icon name="volume_up" /> {idx + 1}
          </button>
        ))}
      </div>

      <div className="listen3-options grid grid-cols-1 md:grid-cols-3 gap-6 justify-center">
        {question.options.map((collage, optIdx) => {
          let borderClass = 'border-primary';
          if (question.userAnswer !== null && question.userAnswer === optIdx) {
            if (question.status === 'correct') {
              borderClass = 'border-green';
            } else if (question.status === 'incorrect') {
              borderClass = 'border-red';
            } else {
              borderClass = 'border-yellow';
            }
          }
          return (
            <button
              key={optIdx}
              className={`collage-btn border transition-all ${borderClass} ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
              onClick={() => handleAnswerWithStop(optIdx)}
              disabled={disabled}
              aria-label={`Vybrat koláž ${optIdx + 1}`}
            >
              <div className="flex flex-row gap-1 justify-center">
                {collage.map((img, imgIdx) => (
                  <img
                    key={imgIdx}
                    src={img}
                    alt={`Obrázek ${imgIdx + 1}`}
                    className="w-32 h-32 object-cover"
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Listen3Play;
